export const PROJECT_EXPORT_TYPE = "pythia_project_config";
export const PROJECT_EXPORT_SCHEMA_VERSION = 1;
export const SUPPORTED_PROJECT_IMPORT_SCHEMA_VERSIONS = [1];

const LEGACY_PROJECT_IMPORT_SCHEMA_VERSION = 1;

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function describeMissingPaths(paths) {
  if (!paths.length) return "";
  const preview = paths.slice(0, 8).join(", ");
  const suffix = paths.length > 8 ? `, and ${paths.length - 8} more` : "";
  return `${preview}${suffix}`;
}

function getPath(value, path) {
  return path.split(".").reduce((cur, segment) => {
    if (!isPlainObject(cur) && !Array.isArray(cur)) return undefined;
    return cur?.[segment];
  }, value);
}

const REQUIRED_CONFIG_PATHS = [
  "projectName",
  "timeZone",
  "biddingZone",
  "address",
  "loc_info",
  "loc_info.lat",
  "loc_info.lon",
  "systemType",
  "settlementType",
  "grid",
  "grid.exportLimitKw",
  "grid.importLimitKw",
  "pvInstalledCap",
  "marketStack",
  "marketParticipation",
  "bess",
  "bess.selected",
  "batteryDetails",
  "feasibility",
  "feasibility.technical",
  "feasibility.economics",
];

export function sanitizeProjectNameForFilename(name) {
  const base = (name || "project")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "");
  return (base || "project").slice(0, 80);
}

export function buildProjectExportFilename(projectName, date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(date.getHours())}-${pad(date.getMinutes())}`;
  return `${sanitizeProjectNameForFilename(projectName)}_pythia_project_config_${stamp}.json`;
}

export function buildProjectExportPayload(project, config, exportedAt = new Date().toISOString()) {
  return {
    export_type: PROJECT_EXPORT_TYPE,
    schema_version: PROJECT_EXPORT_SCHEMA_VERSION,
    exported_at: exportedAt,
    app: "PYTHIA",
    app_version: "0.1.0",
    project: {
      id: project?.id ?? null,
      name: project?.name ?? null,
      config: config ?? {},
    },
  };
}

export function validateProjectExportPayload(payload) {
  if (!isPlainObject(payload)) {
    throw new Error("Invalid project JSON file. Expected a PYTHIA project export object.");
  }

  if (payload.export_type !== PROJECT_EXPORT_TYPE) {
    throw new Error("Invalid project JSON file. Expected export_type \"pythia_project_config\".");
  }

  const schemaVersion = payload.schema_version ?? LEGACY_PROJECT_IMPORT_SCHEMA_VERSION;
  if (!Number.isInteger(schemaVersion)) {
    throw new Error("Invalid project JSON file. schema_version must be an integer.");
  }
  if (!SUPPORTED_PROJECT_IMPORT_SCHEMA_VERSIONS.includes(schemaVersion)) {
    throw new Error(
      `This project JSON uses schema version ${schemaVersion}, but this PYTHIA version supports ${SUPPORTED_PROJECT_IMPORT_SCHEMA_VERSIONS.join(", ")}.`
    );
  }

  if (payload.app !== "PYTHIA") {
    throw new Error("Invalid project JSON file. This file was not exported by PYTHIA.");
  }

  if (!isPlainObject(payload.project)) {
    throw new Error("Invalid project JSON file. Missing project object.");
  }

  if (!isPlainObject(payload.project.config)) {
    throw new Error("Invalid project JSON file. Missing project.config object.");
  }

  const missing = REQUIRED_CONFIG_PATHS.filter((path) => {
    const value = getPath(payload.project.config, path);
    return value === undefined || value === null;
  });

  if (missing.length) {
    throw new Error(
      `This project JSON is missing required current PYTHIA parameters: ${describeMissingPaths(missing)}.`
    );
  }

  return {
    schemaVersion,
    projectName:
      typeof payload.project.name === "string" && payload.project.name.trim()
        ? payload.project.name.trim()
        : "Imported Project",
    config: payload.project.config,
    sourceProjectId: payload.project.id ?? null,
    exportedAt: payload.exported_at ?? null,
  };
}

export const PROJECT_PACKAGE_EXTENSION = ".pythia";

export function buildProjectPackageFilename(projectName, date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(date.getHours())}-${pad(date.getMinutes())}`;
  return `${sanitizeProjectNameForFilename(projectName)}_pythia_project_${stamp}${PROJECT_PACKAGE_EXTENSION}`;
}

export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
